"use client";

import { useEffect } from "react";

/**
 * Service Worker registration.
 *
 * On `dev.boulium.com` we deliberately UNREGISTER any prior SW + clear caches
 * — Next.js dev/Turbopack rebuilds emit fresh chunk hashes constantly, and
 * a stale cache-first SW served outdated chunks (the 2026-05-19 `Failed to
 * fetch` reports). Push features only matter in production anyway.
 *
 * On production hosts the SW is registered normally and an `updatefound`
 * listener triggers `skipWaiting` so new versions activate without a manual
 * "Update" prompt.
 */
export default function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    const host = window.location.hostname;
    const isDevHost =
      host === "dev.boulium.com" ||
      host === "localhost" ||
      host === "127.0.0.1";

    if (isDevHost) {
      // Unregister any previously-installed SW and clear its caches so the
      // dev environment never serves a stale chunk.
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) => Promise.all(regs.map((r) => r.unregister())))
        .catch(() => {});
      if ("caches" in window) {
        caches
          .keys()
          .then((keys) =>
            Promise.all(
              keys
                .filter((k) => k.startsWith("boulium-"))
                .map((k) => caches.delete(k)),
            ),
          )
          .catch(() => {});
      }
      return;
    }

    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        // Auto-activate any waiting worker so users don't get stuck on stale
        // chunks after a deploy.
        if (reg.waiting) {
          reg.waiting.postMessage({ type: "SKIP_WAITING" });
        }
        reg.addEventListener("updatefound", () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              installing.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch(() => {
        // silently ignore — push features will be unavailable
      });
  }, []);
  return null;
}
